import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useEscape, useScrollLock, STOP_PROPAGATION } from './util';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Layer — a full-screen portal (`.ui-root.ui-layer`) with the mockups' scrim
 * (rgba(5,7,11,.72)). Dialog, Drawer and the phone More sheet sit in one.
 * Rendering in a portal means the layer carries its own `.ui-root`, so the
 * tokens + reset apply even though it is outside the shell's DOM.
 *
 * Escape and a scrim click call onClose (unless `dismissable` is false).
 * Focus moves into the panel on open, is kept inside it with Tab, and returns
 * to the opener on close.
 */
export default function Layer({ open, onClose, dismissable = true, labelledBy, label, children }) {
  const panelRef = useRef(null);
  const openerRef = useRef(null);
  const close = useCallback(() => { if (dismissable && onClose) onClose(); }, [dismissable, onClose]);
  useEscape(open, close);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    const id = setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const auto = panel.querySelector('[data-autofocus]');
      const first = auto || panel.querySelector(FOCUSABLE);
      if (first && first.focus) first.focus();
    }, 0);
    return () => {
      clearTimeout(id);
      const opener = openerRef.current;
      if (opener && opener.focus && document.body.contains(opener)) opener.focus();
    };
  }, [open]);

  const onKeyDown = (e) => {
    // Keys typed in a dialog must not reach handlers of the page underneath
    // (React bubbles through the portal along the component tree).
    e.stopPropagation();
    if (e.key !== 'Tab' || !panelRef.current) return;
    const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div className="ui-root ui-layer" {...STOP_PROPAGATION} onKeyDown={onKeyDown}>
      <div className="ui-scrim" onClick={close} aria-hidden="true" />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={labelledBy} aria-label={labelledBy ? undefined : label} style={{ display: 'contents' }}>
        {children}
      </div>
    </div>,
    document.body,
  );
}
